import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  useBorrarCopia, useCopias, useCrearCopia, useRestaurarCopia, type Copia,
} from './api';

/**
 * Copias y respaldo — la copia de la base y la de los archivos.
 *
 * ## Lo que cambia
 *
 * - **Descargar una copia ya no pasa por una URL armada a mano.** Era
 *   `onclick="Utils.downloadAuthed('/api/admin/backups/${Utils.escapeHtml(b.name)}/download', …)"`:
 *   el nombre se escapaba como HTML —no como URL— y se metía dentro de una
 *   cadena entre comillas simples dentro de un atributo. Un nombre con un
 *   apóstrofo rompía el `onclick` entero y el botón dejaba de hacer nada.
 * - **La confirmación de restaurar no se pierde al recargar la lista.** El
 *   diálogo se armaba con el nombre y el tipo pasados por la URL del `onclick`,
 *   así que dependía del HTML pintado; aquí el objeto es el estado.
 * - **Al restaurar, la pantalla dice que hay que recargar** en vez de dejarlo en
 *   un aviso que se va en seis segundos: después de una restauración lo que hay
 *   en pantalla es de otra base de datos.
 */
export function Copias() {
  const consulta = useCopias(true);
  const crear = useCrearCopia();
  const borrar = useBorrarCopia();

  const [borrando, setBorrando] = useState<Copia | null>(null);
  const [restaurando, setRestaurando] = useState<Copia | null>(null);
  const [restaurada, setRestaurada] = useState(false);

  const copias = consulta.data ?? [];

  return (
    <>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-4 font-bold text-brand-ink">Copias de seguridad</h2>
          <p className="mt-1 text-3 text-muted">
            Copia completa de la base de datos. Se genera automáticamente cada día —se conservan
            las últimas treinta— y puedes crear una manual cuando quieras.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            loading={crear.isPending && crear.variables === true}
            onClick={() => crear.mutate(true, {
              onSuccess: (r) => Utils.toast(
                `Copia de archivos creada (${r.total_objects ?? 0} archivos)`, 'success'),
              onError: (e) => Utils.toast((e as Error).message, 'error'),
            })}
          >
            <i className="bi bi-images" aria-hidden="true" /> Copia de archivos
          </Button>
          <Button
            size="sm"
            loading={crear.isPending && crear.variables === false}
            onClick={() => crear.mutate(false, {
              onSuccess: (r) => Utils.toast(
                `Copia creada (${r.total_rows ?? 0} filas)`, 'success'),
              onError: (e) => Utils.toast((e as Error).message, 'error'),
            })}
          >
            <i className="bi bi-plus-circle" aria-hidden="true" /> Crear copia (BD)
          </Button>
        </div>
      </header>

      <p className="mb-4 flex items-start gap-2 rounded-sm border border-danger bg-danger/8 p-3 text-2 text-danger-ink">
        <i className="bi bi-exclamation-triangle mt-0.5 shrink-0" aria-hidden="true" />
        <span>
          <strong>Restaurar</strong> reemplaza TODOS los datos actuales por los de la copia. Antes
          de hacerlo se crea automáticamente una copia de seguridad. Es una operación de
          recuperación ante desastres, no una forma de deshacer un cambio.
        </span>
      </p>

      {restaurada && (
        <p
          className="mb-4 flex flex-wrap items-center gap-2 rounded-sm border border-warn bg-warn/10 p-3 text-3 text-warn-ink"
          role="status"
        >
          <i className="bi bi-arrow-clockwise" aria-hidden="true" />
          <strong>Restauración terminada.</strong> Lo que ves en pantalla es de antes: recarga el
          portal para trabajar con los datos restaurados.
          <Button size="sm" variant="secondary" onClick={() => location.reload()}>
            Recargar ahora
          </Button>
        </p>
      )}

      {consulta.isPending ? (
        <SkeletonFilas filas={5} columnas={5} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="las copias de respaldo"
          onReintentar={() => void consulta.refetch()}
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-3">
            <caption className="sr-only">Copias de seguridad disponibles</caption>
            <thead className="bg-sunken text-2">
              <tr>
                <th scope="col" className="p-2 text-left font-semibold">Copia</th>
                <th scope="col" className="p-2 text-left font-semibold">Tipo</th>
                <th scope="col" className="p-2 text-left font-semibold">Fecha</th>
                <th scope="col" className="p-2 text-right font-semibold">Tamaño</th>
                <th scope="col" className="p-2 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {!copias.length ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-muted">
                    Aún no hay copias. Crea la primera.
                  </td>
                </tr>
              ) : copias.map((b) => (
                <tr key={b.name} className="border-t border-line">
                  <td className="p-2 font-mono text-2">{b.name}</td>
                  <td className="p-2">
                    <Badge tone={b.kind === 'files' ? 'info' : 'neutral'}>
                      {b.kind === 'files' ? 'Archivos' : 'Base de datos'}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap p-2 text-2 text-muted">
                    {b.created_at ? Utils.fmtDatetime(b.created_at) : '—'}
                  </td>
                  <td className="p-2 text-right text-2 tabular-nums">{tamano(b.size)}</td>
                  <td className="whitespace-nowrap p-1 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      // `encodeURIComponent`, no `escapeHtml`: esto es una URL.
                      onClick={() => Utils.downloadAuthed(
                        `/api/admin/backups/${encodeURIComponent(b.name)}/download`, b.name)}
                    >
                      <i className="bi bi-download" aria-hidden="true" />
                      <span className="sr-only">Descargar {b.name}</span>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setRestaurando(b)}>
                      <i className="bi bi-arrow-counterclockwise text-warn-ink" aria-hidden="true" />
                      <span className="sr-only">Restaurar {b.name}</span>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setBorrando(b)}>
                      <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                      <span className="sr-only">Eliminar {b.name}</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {restaurando && (
        <DialogoDeRestauracion
          copia={restaurando}
          onCerrar={() => setRestaurando(null)}
          onHecho={() => { setRestaurando(null); setRestaurada(true); }}
        />
      )}

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar copia"
        descripcion={`«${borrando?.name ?? ''}» se borra del servidor. Si no la has descargado, no se puede recuperar.`}
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!borrando) return;
          borrar.mutate(borrando.name, {
            onSuccess: () => { Utils.toast('Copia eliminada', 'success'); setBorrando(null); },
            onError: (e) => Utils.toast((e as Error).message, 'error'),
          });
        }}
      />
    </>
  );
}

function tamano(n?: number | null): string {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(1)} ${u[i]}`;
}

/** Restaurar pide escribir RESTAURAR a mano. No es ceremonia: es la única
 *  operación del portal que reemplaza todo y no se deshace. */
function DialogoDeRestauracion({ copia, onCerrar, onHecho }: {
  copia: Copia;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const restaurar = useRestaurarCopia();
  const [motivo, setMotivo] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const archivos = copia.kind === 'files';
  const puede = confirmacion.trim().toUpperCase() === 'RESTAURAR';

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Restaurar copia"
      descripcion={archivos
        ? `Se vuelven a subir los archivos —fotos, adjuntos, PDF— de «${copia.name}», sobrescribiendo los actuales.`
        : `Se reemplazan TODOS los datos de la base por los de «${copia.name}». Antes se crea una copia automática.`}
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button
            variant="destructive"
            loading={restaurar.isPending}
            disabled={!puede}
            razonDeshabilitado="Escribe RESTAURAR para confirmar."
            onClick={() => restaurar.mutate(
              { nombre: copia.name, archivos, motivo: motivo.trim() },
              {
                onSuccess: (r) => {
                  Utils.toast(archivos
                    ? `Archivos restaurados (${r.restored_objects ?? 0}).`
                    : `Restauración completa (${r.restored_tables ?? 0} tablas).`, 'success');
                  onHecho();
                },
                onError: (e) => Utils.toast((e as Error).message, 'error'),
              })}
          >
            Restaurar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {restaurar.isPending && (
          <p className="rounded-sm border border-warn bg-warn/10 p-3 text-2 text-warn-ink" role="status">
            Restaurando. No cierres la ventana ni recargues hasta que termine.
          </p>
        )}
        <Field label="Motivo" opcional ayuda="Queda registrado en auditoría con tu nombre.">
          <Input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej. recuperación tras un borrado por error"
          />
        </Field>
        <Field
          label="Escribe RESTAURAR para confirmar"
          ayuda="En mayúsculas o minúsculas, da igual."
        >
          <Input
            value={confirmacion}
            onChange={(e) => setConfirmacion(e.target.value)}
            placeholder="RESTAURAR"
            autoComplete="off"
          />
        </Field>
      </div>
    </Dialog>
  );
}
